import { useCallback, useEffect, useRef } from "react";
import useU2000LocalStorage from "./U2000UseLocalStorage";
import { useU2000AuthContext } from "../context/auth/U2000UseAuthContext";

// Interface pour les paramètres du hook
interface U2000MenuStateHandlersParams {
  luName?: string;
}

// Interface pour l'état du menu
interface U2000MenuState {
  isCollapsed: boolean;
  isPinned: boolean;
}

// Interface de retour du hook
interface U2000MenuStateHandlersReturn {
  menuState: U2000MenuState;
  setMenuCollapsed: (collapsed: boolean) => void;
  setMenuPinned: (pinned: boolean) => void;
  setMenuState: (state: Partial<U2000MenuState>) => void;
  toggleMenuCollapsed: () => void;
  toggleMenuPinned: () => void;
}

// Hook pour la gestion de l'état persistant du menu
export function useU2000MenuStateHandlers(params: U2000MenuStateHandlersParams = {}): U2000MenuStateHandlersReturn {
  const { luName = "default" } = params;
  const { auth } = useU2000AuthContext();

  // Construire une clé localStorage spécifique à l'utilisateur et l'organisation
  const userSpecificKey = auth.isAuthenticated && auth.user.userName ? `${luName}_${auth.user.userName}_${auth.user.organizationId || "noorg"}` : luName;

  // State local persistant pour l'état du menu
  const [menuState, setMenuState] = useU2000LocalStorage<U2000MenuState>(`${userSpecificKey}MenuState`, {
    isCollapsed: false,
    isPinned: true,
  });

  // debug logs removed

  // Référence pour suivre le changement d'utilisateur
  const previousUserKeyRef = useRef<string>(userSpecificKey);
  const previousUserNameRef = useRef<string | null | undefined>(auth.user?.userName);

  // Surveillance des changements d'utilisateur pour réinitialiser l'état du menu
  // Seule une modification du nom d'utilisateur (vrai changement d'utilisateur)
  // doit entraîner la réinitialisation — un simple changement d'organisation ne doit pas.
  useEffect(() => {
    const currentUserKey = userSpecificKey;
    const currentUserName = auth.user?.userName;
    const previousUserName = previousUserNameRef.current;

    if (previousUserName && currentUserName && previousUserName !== currentUserName) {
      // Réinitialiser l'état du menu aux valeurs par défaut
      setMenuState({
        isCollapsed: false,
        isPinned: true,
      });
    }

    // Mettre à jour les références
    previousUserKeyRef.current = currentUserKey;
    previousUserNameRef.current = currentUserName;
    // Déclencher lorsque la clé utilisateur spécifique ou le nom d'utilisateur change
  }, [userSpecificKey, luName, setMenuState, auth.user?.userName]);

  // Si on change d'organisation et que la nouvelle clé n'a pas d'état persistant,
  // s'assurer d'initialiser l'état aux valeurs par défaut (ne pas garder l'état précédent).
  useEffect(() => {
    try {
      const storageKey = `${userSpecificKey}MenuState`;
      const stored = typeof window !== "undefined" ? localStorage.getItem(storageKey) : null;
      if (!stored) {
        setMenuState({ isCollapsed: false, isPinned: true });
      }
    } catch (err) {
      // ignore
    }
  }, [userSpecificKey, setMenuState]);

  // Fonction pour mettre à jour l'état du menu (peut mettre à jour plusieurs propriétés à la fois)
  const updateMenuState = useCallback(
    (updates: Partial<U2000MenuState>) => {
      setMenuState((prevState) => ({
        ...prevState,
        ...updates,
      }));
    },
    [setMenuState]
  );

  // Fonction pour définir l'état pinned
  const setMenuPinned = useCallback(
    (pinned: boolean) => {
      setMenuState((prevState) => ({
        ...prevState,
        isPinned: pinned,
      }));
    },
    [setMenuState]
  );

  // Fonction pour définir l'état collapsed
  const setMenuCollapsed = useCallback(
    (collapsed: boolean) => {
      setMenuState((prevState) => ({
        ...prevState,
        isCollapsed: collapsed,
      }));
    },
    [setMenuState]
  );

  // Fonction pour toggler l'état collapsed
  const toggleMenuCollapsed = useCallback(() => {
    setMenuState((prevState) => ({
      ...prevState,
      isCollapsed: !prevState.isCollapsed,
    }));
  }, [setMenuState]);

  // Fonction pour toggler l'état pinned
  const toggleMenuPinned = useCallback(() => {
    setMenuState((prevState) => ({
      ...prevState,
      isPinned: !prevState.isPinned,
    }));
  }, [setMenuState]);

  return {
    menuState,
    setMenuCollapsed,
    setMenuPinned,
    setMenuState: updateMenuState,
    toggleMenuCollapsed,
    toggleMenuPinned,
  };
}

export default useU2000MenuStateHandlers;
